<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";

import type { Profile } from "@/types/profile";
import { LoginState } from "@/types/enums";

import { useComponentErrorCapture, getErrorMessage } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { useModal } from "@/mixins/modal";

import { authStorage } from "@/stores/auth";
import { profileStore } from "@/stores/profile";
import { profilesStore } from "@/stores/profiles";
import { addErrorBottom } from "@/stores/messages";
import { appStateStore } from "@/stores/appState";
import { systemBaseStore } from "@/stores/systemBase";

import Modal from "@/components/elements/Modal.vue";
import Avatar from "@/components/ui/Avatar.vue";
import UCInput from "@/components/ui/UCInput.vue";
import Dialog from "@/components/elements/Dialog.vue";
import ManageProfiles from "@/components/profile/ManageProfiles.vue";
import EditProfile from "@/components/profile/EditProfile.vue";
import SelectedIcon from "@/components/elements/icon/SelectedIcon.vue";
import Logout from "@/components/elements/Logout.vue";

const { sleep } = useTiming();
useComponentErrorCapture();

const props = defineProps({
  mobileView: {
    type: Boolean,
    default: false,
  },
});


const emit = defineEmits(["logout"]);

const instanceUid = getCurrentInstance()?.uid || Math.floor(Math.random() * 1000);
const auth = authStorage();
const storage = profileStore();
const profilesStorage = profilesStore();
const appState = appStateStore();
const systemBaseStorage = systemBaseStore();

const router = useRouter();
const { registerOpenModal, unregisterModal } = useModal();

const dialogAlreadyActive = ref();
const dialogEnterPwd = ref();
const elLogout = ref();
const profiles = ref<Profile[]>([]);
const activeProfiles = ref<Profile[]>([]);
const profileToSwitch = ref<Profile | null>(null); 
const modalEdit = ref();
const open = ref(false);
const pin = ref('');
const errorMessageEnterPin = ref('');
const menuProfilePopup = ref();
const showProfileChanged = ref(false);

watch(profiles, async () => {
  validateProfilesData();
})

watch(open, async (val) => {
  if (val == true) {
    registerOpenModal(instanceUid);
  } else {
    unregisterModal(instanceUid);
  }
})

watch(activeProfiles, async (val, oldVal) => {
  if (showProfileChanged.value == false &&
    props.mobileView == false && 
    val && val.length > 0 && 
    oldVal && oldVal.length > 0 && 
    val[0]?.profile_id != oldVal[0]?.profile_id) {
    showProfileChanged.value = true;
    await sleep(3000)
    showProfileChanged.value = false;
  }
})

auth.$subscribe(async (mutation, state) => {
  if (state.authenticated == LoginState.AUTHORISED && profiles.value.length < 1) {
    await getProfilesData();
  }
})

profilesStorage.$subscribe((mutation, state) => {
  if (state.profiles) {
    profiles.value = state.profiles;
    activeProfiles.value = state.activeProfiles;
  }
})

appState.$subscribe((mutation, state) => {
  if (open.value && !state.modalPool.includes(instanceUid.toString())) {
    open.value = false;
  }
});

const mainClasses = computed(() => {
  return props.mobileView == true ? "menu-profile--mobile" : "menu-profile--desktop";
})

function addProfile() {
  modalEdit.value.open();
}

function editProfile(id: string) {
  modalEdit.value.open(id);
}

async function getProfilesData() {
  try {
    profiles.value = await profilesStorage.getAll(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
  try {
    activeProfiles.value = await profilesStorage.getAllActive(true);
  } catch (e: any) {
    if (e && e.status && e.status == 404) {
      console.error(e);
    } else {
    addErrorBottom(e);
    }
  }
}

// Validate array of profiles
async function validateProfilesData() {
  if (profiles.value.length > 1 && 'profile_id' in profiles.value[0] == false) {
    await getProfilesData();
  }
}

async function switchProfile(profile: Profile | null, inputPin: string = '') {
  if (profile == null) {
    return false;
  }

  if (isActive(profile)) {
    return showAlreadyActive();
  }

  if (activeProfiles.value &&
    activeProfiles.value.length > 0 && 
    activeProfiles.value[0] &&
    activeProfiles.value[0].restricted === true &&
    inputPin.length < 4) {
      return showEnterPassword(profile);
  }

  try {
    await profilesStorage.switch(profile.profile_id, inputPin);
    storage.setProfile(profile.profile_id);
    dialogEnterPwd.value.close();
    profileToSwitch.value = null;
    pin.value = '';
  } catch (e: any) {
    if (inputPin.length > 1) {
      errorMessageEnterPin.value = getErrorMessage(e, 'profile.switch')?.message;
    } else {
      addErrorBottom(e);
    }
  }
}

function isActive(profile: Profile) {
  return activeProfiles.value.some(p => p.profile_id === profile.profile_id);
}

function showAlreadyActive() {
  dialogAlreadyActive.value.open();
}

function showEnterPassword(profile: Profile) {
  errorMessageEnterPin.value = '';
  profileToSwitch.value = profile;
  pin.value = '';
  dialogEnterPwd.value.open();
}

function clearInputError() {
  if (errorMessageEnterPin.value.length > 0) {
    errorMessageEnterPin.value = '';
  }
}

function startLogout() {
  if (elLogout.value) {
    elLogout.value.startLogout();
  }
}

async function logout() {
  emit('logout');
  open.value = false;
}

onMounted(async () => {
  if (auth.authenticated == LoginState.AUTHORISED) {
    await getProfilesData();
  }
})

onUnmounted(() => {
  unregisterModal(instanceUid);
});

</script>
<template>
  <div class="menu-profile" :class="mainClasses">
    <button
      @click="open = true"
      class="button button--tertiary button--with-icon menu-profile__trigger"
      :class="{'menu-profile__trigger--no-profile': !activeProfiles || activeProfiles.length < 1}">
      <template v-if="activeProfiles && activeProfiles.length > 0">
        <span>{{ activeProfiles[0].name }}</span>
        <SelectedIcon :icon="activeProfiles[0].icon ? activeProfiles[0].icon : 'fa-light fa-user'" :fallback-icon="'fa-light fa-user'" />
      </template>
      <SelectedIcon v-else :icon="'fa-light fa-user'" :fallback-icon="'fa-light fa-user'" />
    </button>
    <Transition name="grow">
      <div
        v-show="open || props.mobileView == true"
        ref="menuProfilePopup"
        :class="{'opened': open}"
        class="menu-profile__body"
      >
        <div class="menu-profile__body__title">{{ $t("profile.your_profiles") }}</div>
        <button class="button button--secondary button--icon button--icon--small button-close" @click="open = false"><i  class="fa-regular fa-close"></i></button>
        <ul class="menu-profile__body__list">
          <li
            v-for="profile in profiles">
            <Avatar
              @click="switchProfile(profile)"
              :name="profile.name"
              :icon="profile.icon"
              :restricted="profile.restricted"
              :active="isActive(profile)"
              :simple="true"
              :small="true"/>
          </li>
          <li>
            <button @click="addProfile" class="button-add-profile">
              <i class="icon-container fa-light fa-plus"></i>
              <span>{{ $t("profile.add_profile") }}</span>
            </button>
          </li>
        </ul>
        <div class="menu-profile__body__footer">
          <p>{{ $t("profile.select_manage_profile") }}</p>
          <div class="menu-profile__body__footer__actions">
            <ManageProfiles
              @add-profile="addProfile"
              @edit-profile="editProfile"
              @closed="open = false"
              :profiles="profiles"
              :active-profiles="activeProfiles"
            />

            <button @click="startLogout" class="button button--secondary">
              {{ $t("ui.logout") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
    <Transition name="opacity">
      <div
        v-show="open || props.mobileView == true"
        @click="open = false"
        class="menu-profile__background">
      </div>
    </Transition>

    <Teleport v-if="!mobileView && activeProfiles && activeProfiles.length > 0" to="body">
      <Modal
        :show="showProfileChanged"
        :height="'100%'"
        :width="'32.5rem'"
        class="modal--profile-changed"
        name="profile-changed"
        :closeable="true"
      >
        <Avatar
          :name="activeProfiles[0]?.name"
          :icon="activeProfiles[0]?.icon"
          :active="true"
          :simple="true"
          :small="false"
        />
        <template #footer>
          <p class="modal--profile-changed__description">{{ $t("profile.profile_has_changed") }}</p>
        </template>
      </Modal>
    </Teleport>
    
    <Teleport to="body">
      <EditProfile
        ref="modalEdit"
      />
    </Teleport>
    <Dialog
      ref="dialogAlreadyActive"
      :title="$t('profile.already_active.title')"
      :text="$t('profile.already_active.info')"
      :submit-text="$t('ui.dismiss')"
      :text-center="true"
      :icon="'fa-thin fa-face-monocle'"
      :icon-type="'red'" />
    <Dialog
      ref="dialogEnterPwd"
      @submit="switchProfile(profileToSwitch, pin)"
      :disable-close-on-submit="true"
      :title="$t('profile.enter_admin_pwd.title')"
      :text="$t('profile.enter_admin_pwd.info')"
      :submit-text="$t('ui.enter')"
      :cancel-text="$t('ui.cancel')">
      <template #extra>
        <UCInput
          v-model="pin"
          :type="'password'"
          :label="$t('profile.password')"
          :full-w="true"
          :error-message="errorMessageEnterPin"
          :focus="true"
          @input="clearInputError"
          @submit="switchProfile(profileToSwitch, pin)"
        />
      </template>
    </Dialog>
    
    <Logout ref="elLogout" @logout="logout" />
  </div>
</template>